import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, InlineLoading } from "@carbon/react";
import { Download, View } from "@carbon/icons-react";
import { useOcPdf } from "../../../hooks/useOcActions";

interface OcDocumentAttachmentProps {
  ocId: string;
  ocCode: string;
}

/**
 * Compact "the generated OC is attached" row for the Coordinate modal: the OC PDF (same
 * `GET /purchase-orders/{id}/pdf` bytes the Documento tab renders) offered as View (new tab) +
 * Download, so the buyer can see the purchase order they are coordinating a delivery against. A
 * full inline iframe would crowd the form modal, so this stays an attachment row, not a viewer.
 */
const OcDocumentAttachment = ({ ocId, ocCode }: OcDocumentAttachmentProps) => {
  const { t } = useTranslation();
  const { data: blob, isLoading, isError, refetch } = useOcPdf(ocId, true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setBlobUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  const download = () => {
    if (!blobUrl) return;
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${ocCode}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="coordinate-oc__oc-doc">
      <span className="cds--label coordinate-oc__oc-doc-label">
        {t("purchaseOrders.coordinate.ocDocument")}
      </span>
      <div className="coordinate-oc__oc-doc-row">
        <span className="coordinate-oc__oc-doc-name">{ocCode}.pdf</span>
        {isLoading ? (
          <InlineLoading
            description={t("purchaseOrders.detail.document.loading")}
          />
        ) : isError || !blobUrl ? (
          <Button kind="ghost" size="sm" onClick={() => refetch()}>
            {t("purchaseOrders.detail.document.retry")}
          </Button>
        ) : (
          <div className="coordinate-oc__oc-doc-actions">
            <Button
              kind="ghost"
              size="sm"
              renderIcon={View}
              onClick={() => window.open(blobUrl, "_blank")}
            >
              {t("purchaseOrders.detail.document.view")}
            </Button>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Download}
              onClick={download}
            >
              {t("purchaseOrders.detail.document.download")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OcDocumentAttachment;
